import React, { useEffect, useState } from 'react';
import { Button, Table, Skeleton, Paper, Stack, Menu, ActionIcon, Group as MantineGroup, Text } from '@mantine/core';
import Header from '~src/components/Header';
import { useGroups } from '~src/api_hooks/groups';
import { useNavigate } from 'react-router-dom';
import { LogOut, Trash, MoreHorizontal, ArrowLeft, UserPlus, User, Settings } from 'lucide-react';
import Modal from '~src/components/Modal';
import JoinGroup from '~src/components/JoinGroup';
import CreateGroup from '~src/components/CreateGroup';
import EditProfileModal from '~src/components/EditProfileModal';
import GroupSettingsModal from '~src/components/GroupSettingsModal';
import AvatarWithFallback from '~src/components/AvatarWithFallback';
import { UserRole } from '~src/types';
import { peep_client } from '~/src/api/peep';
import { Uuid, UuidString } from '~src/utils/UUID';
import { notifications } from '@mantine/notifications';
import { Group } from '~/src/types/groups';

export default function Onboarding() {
  const groups = useGroups();
  const navigate = useNavigate();

  const [joinGroupOpen, setJoinGroupOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [editProfileModal, setEditProfileModal] = useState<{
    group: Group;
    currentAlias: string;
    currentProfileImage: string | null;
  } | null>(null);
  const [groupSettingsModal, setGroupSettingsModal] = useState<{ group: Group } | null>(null);

  const openEditProfileModal = (group: Group): void => {
    setEditProfileModal({
      group,
      currentAlias: group.user_alias,
      currentProfileImage: group.user_profile_image,
    });
  };

  const handleDeleteGroup = async (groupId: UuidString, groupName: string) => {
    try {
      await peep_client.groups.deleteGroup(groupId);
      notifications.show({
        title: 'Success',
        message: `Group "${groupName}" deleted successfully`,
        color: 'green',
      });
      groups.mutate();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete group',
        color: 'red',
      });
    }
  };

  const handleLeaveGroup = async (groupId: UuidString, groupName: string) => {
    try {
      await peep_client.groups.leaveGroup(groupId);
      notifications.show({
        title: 'Success',
        message: `Left group "${groupName}" successfully`,
        color: 'green',
      });
      groups.mutate();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to leave group',
        color: 'red',
      });
    }
  };

  const handleInviteGroup = async (groupId: UuidString) => {
    try {
      const token = await peep_client.groups.createInvite(groupId);
      // Construct the full invite URL (assuming it's a join link with the token)
      // or just copy the token if that's what's expected.
      // Based on checking just the token for now, will copy path /join?token=...
      const inviteLink = `${window.location.origin}/join?token=${token}`;
      await navigator.clipboard.writeText(inviteLink);
      notifications.show({
        title: 'Success',
        message: 'Invite link copied to clipboard',
        color: 'green',
      });
    } catch (error) {
      console.error(error);
      notifications.show({
        title: 'Error',
        message: 'Failed to create invite',
        color: 'red',
      });
    }
  };

  useEffect(() => {}, [groups]);

  return (
    <div>
      <Header pageName="Group Management"></Header>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '1rem 2rem',
          backgroundColor: 'var(--global-gray-lighter-7)',
          minHeight: 'calc(100dvh - 60px)',
          flexDirection: 'column',
          alignItems: 'center',
          // gap: '1rem'
        }}
      >
        <div style={{ width: '100%', maxWidth: '1200px', display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            variant="subtle"
            color="gray"
            leftSection={<ArrowLeft size={16} />}
            onClick={() => navigate('/feed')}
            style={{ paddingLeft: 0 }}
          >
            Back to Feed
          </Button>
        </div>
        <Paper
          style={{
            width: '100%',
            maxWidth: '1200px',
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            border: '1px solid #f0f0f0',
            flexGrow: 1,
          }}
        >
          <Stack gap="lg">
            <Skeleton visible={groups.isLoading || groups.isValidating}>
              <Table
                style={{
                  backgroundColor: 'white',
                }}
                highlightOnHover
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Group</Table.Th>
                    <Table.Th>My Profile</Table.Th>
                    <Table.Th>Role</Table.Th>
                    <Table.Th></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {groups.data === undefined ? (
                    <Table.Tr></Table.Tr>
                  ) : (
                    groups.data.map((g) => (
                      <Table.Tr key={g.id.toString()}>
                        <Table.Td>
                          <MantineGroup gap="sm">
                            <AvatarWithFallback imageUrl={g.group_image} seedId={g.id.toString()} size={40} />
                            <Text>{g.display_name}</Text>
                          </MantineGroup>
                        </Table.Td>
                        <Table.Td>
                          <MantineGroup gap="sm">
                            <AvatarWithFallback
                              imageUrl={g.user_profile_image}
                              seedId={`${g.id.toString()}-${g.user_alias}`}
                              size={40}
                            />
                            <Text>{g.user_alias}</Text>
                          </MantineGroup>
                        </Table.Td>
                        <Table.Td>{g.user_role}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Menu shadow="md" width={200} position="bottom-end">
                            <Menu.Target>
                              <ActionIcon variant="default" color="gray">
                                <MoreHorizontal size={20} />
                              </ActionIcon>
                            </Menu.Target>

                            <Menu.Dropdown>
                              <Menu.Item leftSection={<User size={14} />} onClick={() => openEditProfileModal(g)}>
                                Edit My Profile
                              </Menu.Item>

                              {g.user_role === UserRole.Values.owner && (
                                <Menu.Item
                                  leftSection={<Settings size={14} />}
                                  onClick={() => setGroupSettingsModal({ group: g })}
                                >
                                  Group Settings
                                </Menu.Item>
                              )}

                              {g.user_role === UserRole.Values.owner && (
                                <Menu.Item leftSection={<UserPlus size={14} />} onClick={() => handleInviteGroup(g.id)}>
                                  Invite User
                                </Menu.Item>
                              )}

                              {g.user_role === UserRole.Values.member && (
                                <Menu.Item
                                  color="red"
                                  leftSection={<LogOut size={14} />}
                                  onClick={() => handleLeaveGroup(g.id, g.display_name)}
                                >
                                  Leave Group
                                </Menu.Item>
                              )}

                              {g.user_role === UserRole.Values.owner && (
                                <Menu.Item
                                  color="red"
                                  leftSection={<Trash size={14} />}
                                  onClick={() => handleDeleteGroup(g.id, g.display_name)}
                                >
                                  Delete Group
                                </Menu.Item>
                              )}
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Skeleton>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Button
                onClick={() => {
                  setCreateGroupOpen(true);
                }}
                style={{
                  backgroundColor: 'var(--global-main)',
                  fontFamily: 'Lato',
                  fontWeight: '600',
                  borderRadius: '8px',
                }}
                fullWidth
              >
                Create Group
              </Button>
              <Button
                onClick={() => {
                  setJoinGroupOpen(true);
                }}
                variant="outline"
                style={{
                  color: 'var(--global-main)',
                  borderColor: 'var(--global-main)',
                  fontFamily: 'Lato',
                  fontWeight: '600',
                  borderRadius: '8px',
                }}
                fullWidth
              >
                Join Group
              </Button>
            </div>
          </Stack>
        </Paper>
      </div>
      {joinGroupOpen && (
        <Modal
          onClose={() => {
            setJoinGroupOpen(false);
          }}
        >
          <JoinGroup />
        </Modal>
      )}
      {createGroupOpen && (
        <Modal
          onClose={() => {
            setCreateGroupOpen(false);
          }}
        >
          <CreateGroup />
        </Modal>
      )}
      {editProfileModal && (
        <EditProfileModal
          opened={true}
          onClose={() => {
            setEditProfileModal(null);
            groups.mutate();
          }}
          group={editProfileModal.group}
          currentAlias={editProfileModal.currentAlias}
          currentProfileImage={editProfileModal.currentProfileImage}
        />
      )}
      {groupSettingsModal && (
        <GroupSettingsModal
          opened={true}
          onClose={() => {
            setGroupSettingsModal(null);
            groups.mutate();
          }}
          group={groupSettingsModal.group}
        />
      )}
    </div>
  );
}
